import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Star, Quote, PenLine } from "lucide-react";
import { TESTIMONIALS } from "./data";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stars = ({ value, onChange }) => (
  <div className="flex items-center gap-1" data-testid="review-rating">
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        aria-label={`${n} star`}
        data-testid={`star-${n}`}
        onClick={() => onChange && onChange(n)}
        className={onChange ? "cursor-pointer" : "cursor-default"}
      >
        <Star
          size={onChange ? 26 : 16}
          className={n <= value ? "text-terracotta fill-terracotta" : "text-line"}
        />
      </button>
    ))}
  </div>
);

export const Reviews = () => {
  const [approved, setApproved] = useState([]);
  const [form, setForm] = useState({ name: "", location: "", rating: 5, quote: "" });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/reviews/approved`);
      setApproved(data);
    } catch (e) {
      /* silent */
    }
  };
  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.quote) {
      toast.error("Please add your name and a short review.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/reviews`, form);
      toast.success("Thank you! Your review has been sent for approval.");
      setForm({ name: "", location: "", rating: 5, quote: "" });
    } catch (err) {
      toast.error("Couldn't submit right now. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const sampleAsReview = TESTIMONIALS.map((t) => ({
    id: `sample-${t.name}`,
    name: t.name,
    location: t.place,
    quote: t.quote,
    rating: 5,
  }));
  const all = [...approved, ...sampleAsReview];

  const inputCls =
    "w-full bg-transparent border-b border-line py-3 font-body text-bone text-lg placeholder:text-stone/60 focus:outline-none focus:border-terracotta transition-colors duration-300";

  return (
    <section id="reviews" data-testid="reviews-section" className="bg-surface py-24 md:py-40 border-t border-line">
      <div className="mx-auto max-w-[1600px] px-6 md:px-10">
        <Reveal>
          <p className="font-heading text-xs uppercase tracking-[0.3em] font-bold text-terracotta mb-6">
            [ 05 ] — Word of mouth
          </p>
          <h2 className="font-heading font-black uppercase text-bone tracking-tighter leading-[0.85] text-5xl md:text-7xl lg:text-8xl max-w-5xl mb-6">
            What our clients say
          </h2>
          <p className="font-body font-light text-stone text-base md:text-lg max-w-xl mb-16">
            Real feedback from Dublin homeowners. Had us in? We'd love to hear how it went —
            leave a review below and it'll appear here once approved.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Reviews list */}
          <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-6 content-start">
            {all.map((r, i) => (
              <Reveal key={r.id} delay={(i % 4) * 0.06}>
                <figure
                  data-testid="review-card"
                  className="h-full border border-line bg-ink p-8 flex flex-col justify-between hover:bg-surfaceHover transition-colors duration-500"
                >
                  <div>
                    <Quote size={28} className="text-terracotta mb-5" />
                    <Stars value={r.rating || 5} />
                    <blockquote className="font-heading text-lg md:text-xl font-medium text-bone leading-snug tracking-tight mt-5 mb-8">
                      "{r.quote}"
                    </blockquote>
                  </div>
                  <figcaption>
                    <div className="font-heading font-bold uppercase text-sm tracking-[0.15em] text-bone">
                      {r.name}
                    </div>
                    {r.location ? (
                      <div className="font-body font-light text-stone text-sm mt-1">{r.location}</div>
                    ) : null}
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>

          {/* Review form */}
          <div className="lg:col-span-5 lg:col-start-8">
            <Reveal delay={0.15}>
              <div className="border border-line bg-ink p-8 md:p-10 lg:sticky lg:top-28">
                <div className="flex items-center gap-3 mb-8">
                  <PenLine size={20} className="text-terracotta" />
                  <h3 className="font-heading text-2xl md:text-3xl font-extrabold uppercase text-bone tracking-tight">
                    Leave a review
                  </h3>
                </div>
                <form data-testid="review-form" onSubmit={submit} className="space-y-7">
                  <input
                    data-testid="review-name"
                    className={inputCls}
                    placeholder="Your name *"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  />
                  <input
                    data-testid="review-location"
                    className={inputCls}
                    placeholder="Area (e.g. Malahide, Dublin)"
                    value={form.location}
                    onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
                  />
                  <div>
                    <label className="font-heading text-xs uppercase tracking-[0.2em] font-bold text-stone block mb-3">
                      Your rating
                    </label>
                    <Stars value={form.rating} onChange={(n) => setForm((f) => ({ ...f, rating: n }))} />
                  </div>
                  <textarea
                    data-testid="review-message"
                    rows={4}
                    className={`${inputCls} resize-none`}
                    placeholder="How did we do? *"
                    value={form.quote}
                    onChange={(e) => setForm((f) => ({ ...f, quote: e.target.value }))}
                  />
                  <button
                    type="submit"
                    data-testid="review-submit"
                    disabled={loading}
                    className="w-full font-heading text-xs uppercase tracking-[0.2em] font-bold bg-bone text-ink px-8 py-4 hover:bg-terracotta hover:text-bone transition-colors duration-300 disabled:opacity-50"
                  >
                    {loading ? "Submitting…" : "Submit Review"}
                  </button>
                  <p className="font-body font-light text-stone/70 text-xs">
                    Reviews are published only after approval by MG Tiling Pro.
                  </p>
                </form>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
